'use client'

import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import { Drawer } from '@/components/Drawer'
import { Media } from '@/components/Media'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { User } from '@/payload-types'

import { CelebrationStep } from './components/CelebrationStep'
import { CurrentForm } from './components/profile/CurrentForm'
import { ProfileStep, ProfileSteps } from './components/profile/ProfileStep'
// Re-instate subscriptions: uncomment import and add 'subscriptions' to GuidedIntroSteps
// import { SubscriptionStep } from './components/subscriptions/SubscriptionStep'
import { StagingStep } from './components/StagingStep'
import { StepFooter, stepOrders } from './components/StepFooter'
import { TopicStep } from './components/topics/TopicStep'
import { Confettiful } from './confettiful'

import type { StepDirection } from './components/StepFooter'

export type GuidedIntroSteps = 'celebration' | 'profile' | 'topics' // | 'subscriptions'

const CurrentStep = ({
  currentStep,
  user,
  setCurrentProfileStep,
  setOpenDrawer,
  openDrawer,
  currentProfileStep,
  onUserUpdated,
}: {
  currentStep: GuidedIntroSteps
  user: User
  setCurrentProfileStep: (step: ProfileSteps) => void
  setOpenDrawer: (open: boolean) => void
  openDrawer: boolean
  currentProfileStep: ProfileSteps
  onUserUpdated?: (updatedUser?: User) => Promise<void>
  // onUserUpdated?: () => void
}) => {
  switch (currentStep) {
    case 'celebration':
      return <CelebrationStep />
    case 'profile':
      return (
        <ProfileStep
          user={user}
          setCurrentProfileStep={setCurrentProfileStep}
          setOpenDrawer={setOpenDrawer}
          openDrawer={openDrawer}
          currentProfileStep={currentProfileStep}
        />
      )
    case 'topics':
      return (
        <TopicStep
          user={user}
          setOpenDrawer={setOpenDrawer}
          openDrawer={openDrawer}
          onUserUpdated={onUserUpdated}
        />
      )
    // Re-instate subscriptions: uncomment case and SubscriptionStep
    // case 'subscriptions':
    //   return <SubscriptionStep user={user} onUserUpdated={onUserUpdated} />
  }
}

export const GuidedIntro = ({
  user,
  onUserUpdated,
  showMembershipCheckoutSuccess = false,
}: {
  user: User
  onUserUpdated?: (updatedUser?: User) => Promise<void>
  showMembershipCheckoutSuccess?: boolean
  // onUserUpdated?: () => void
}) => {
  const includeCelebration = showMembershipCheckoutSuccess
  const [currentStep, setCurrentStepState] = useState<GuidedIntroSteps>(
    includeCelebration ? 'celebration' : 'profile',
  )
  const [openDrawer, setOpenDrawer] = useState(false)
  const [currentProfileStep, setCurrentProfileStep] = useState<ProfileSteps>('profile')
  const [direction, setDirection] = useState<StepDirection | null>(null)
  const confettiMountRef = useRef<HTMLDivElement>(null)
  const confettiRef = useRef<Confettiful | null>(null)

  useEffect(() => {
    if (currentStep !== 'celebration' || !confettiMountRef.current) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const instance = new Confettiful(confettiMountRef.current)
    confettiRef.current = instance

    return () => {
      instance.destroy()
      if (confettiRef.current === instance) {
        confettiRef.current = null
      }
    }
  }, [currentStep])

  const setCurrentStep = (step: GuidedIntroSteps, dir?: StepDirection) => {
    if (dir) setDirection(dir)
    setCurrentStepState(step)
    if (dir) setTimeout(() => setDirection(null), 400)
  }

  const previousStep = stepOrders[currentStep].previous
  const nextStep = stepOrders[currentStep].next

  const handleCelebrationNext = () => {
    confettiRef.current?.fadeOut(() => {
      setCurrentStep('profile', 'next')
    })
  }

  return (
    <div className="h-screen w-screen">
      <div
        ref={confettiMountRef}
        className="pointer-events-none fixed inset-0 z-40"
        aria-hidden
      />
      <div className="absolute top-0 left-0 z-0 size-full bg-meltwater-50">
        <Media
          fill
          resource={'/media/image-bg-contact.jpg'}
          alt=""
          htmlelement={null}
          pictureClassName="relative z-0 block h-full w-screen object-cover"
          imgClassName="relative z-0 h-full w-screen object-cover"
        />
        <div className="absolute bottom-0 left-0 z-10 h-1/2 w-full bg-meltwater-50 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-7xl">
        {previousStep && (
          <StagingStep position="left" direction={direction}>
            <CurrentStep
              currentStep={previousStep as GuidedIntroSteps}
              user={user}
              setCurrentProfileStep={setCurrentProfileStep}
              setOpenDrawer={setOpenDrawer}
              openDrawer={openDrawer}
              currentProfileStep={previousStep as ProfileSteps}
              onUserUpdated={onUserUpdated}
            />
          </StagingStep>
        )}
        <Dialog open={openDrawer ? false : true}>
          <DialogContent
            overlayVariant="blur"
            className="border-none bg-neutral-100"
            customCloseButton
          >
            <DialogTitle className="hidden">Let&apos;s get to know you a bit better</DialogTitle>
            <DialogDescription className="sr-only">
              During the next steps, we&apos;ll help you complete your MAHA Action profile.
            </DialogDescription>
            <DialogClose asChild disabled hidden>
              &nbsp;
            </DialogClose>
            <div className="overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentStep}
                  initial={{
                    opacity: 0,
                    x: direction === 'next' ? 24 : direction === 'previous' ? -24 : 0,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{
                    opacity: 0,
                    x: direction === 'next' ? -24 : direction === 'previous' ? 24 : 0,
                  }}
                  transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                  className="relative z-0 min-h-0"
                >
                  <CurrentStep
                    currentStep={currentStep}
                    user={user}
                    setCurrentProfileStep={setCurrentProfileStep}
                    setOpenDrawer={setOpenDrawer}
                    openDrawer={openDrawer}
                    currentProfileStep={currentProfileStep}
                    onUserUpdated={onUserUpdated}
                  />
                </motion.div>
              </AnimatePresence>
              <StepFooter
                user={user}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                onUserUpdated={onUserUpdated}
                onNext={currentStep === 'celebration' ? handleCelebrationNext : undefined}
              />
            </div>
          </DialogContent>
        </Dialog>
        {nextStep && (
          <StagingStep position="right" direction={direction}>
            <CurrentStep
              currentStep={nextStep as GuidedIntroSteps}
              user={user}
              setCurrentProfileStep={setCurrentProfileStep}
              setOpenDrawer={setOpenDrawer}
              openDrawer={openDrawer}
              currentProfileStep={nextStep as ProfileSteps}
              onUserUpdated={onUserUpdated}
            />
          </StagingStep>
        )}
      </div>
      {openDrawer && (
        <Drawer
          open={openDrawer}
          setOpen={setOpenDrawer}
          ariaTitle="MAHA Guided Introduction"
          fullWidth={true}
        >
          <CurrentForm
            currentProfileStep={currentProfileStep}
            user={user}
            setOpenDrawer={setOpenDrawer}
            setCurrentProfileStep={setCurrentProfileStep}
            onUserUpdated={onUserUpdated}
          />
        </Drawer>
      )}
    </div>
  )
}
